import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Button, Card, Input } from '../../components/primitives/index.js'
import { validateFullName } from '../../../worker/src/names.js'
import SEO from '../../components/SEO.jsx'
import { useCustomer } from '../../context/CustomerContext.jsx'
import { useProducts } from '../../context/ProductsContext.jsx'
import { customerApi } from '../../lib/customerApi.js'
import { DEFAULT_TERMS_CONSENT, DEFAULT_TERMS_LABEL } from '../../config/terms.js'
import './AccountAuth.css'

const TITLES = {
  login: { title: 'Sign In', sub: 'Access your orders, addresses, and account settings.' },
  register: { title: 'Create Account', sub: 'Track orders, save addresses, and manage everything in one place.' },
  forgot: { title: 'Reset Password', sub: "We'll email you a secure link to choose a new password." },
}

const VISUAL_COPY = {
  login: { eyebrow: 'Member Access', line1: 'Your research,', line2: 'unlocked.', body: 'Order history, saved addresses, and rewards — all in one secure dashboard.' },
  register: { eyebrow: 'New Account', line1: 'Join the', line2: 'research desk.', body: 'Track shipments, save addresses, and earn rewards on every order.' },
  forgot: { eyebrow: 'Account Recovery', line1: 'Lost your', line2: 'credentials?', body: "We'll send a secure, one-time link to get you back in." },
}

const VISUAL_STATS = [
  { label: 'Encryption', value: 'AES-256' },
  { label: 'Uptime', value: '99.98%' },
  { label: 'Members', value: '2,000+' },
]

const fieldGroup = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
}
const field = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } },
}

function safeNext(raw) {
  if (typeof raw !== 'string' || !raw.startsWith('/') || raw.startsWith('//')) return null
  return raw
}

/**
 * Unified login/register/forgot-password page — mirrors Prod's
 * AccountAuthPage.jsx `mode` prop contract exactly (see App.jsx routes).
 */
export default function AccountAuthPage({ mode = 'login' }) {
  const { customer, login, register } = useCustomer()
  const { customerAccounts } = useProducts()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  const accountsEnabled = customerAccounts?.enabled !== false
  const registrationOpen = customerAccounts?.allowRegistration !== false

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [nameError, setNameError] = useState({})
  const [email, setEmail] = useState(params.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [loading, setLoading] = useState(false)
  const [magicSent, setMagicSent] = useState(false)
  // Never pre-ticked: a box the customer did not tick is not an agreement.
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [termsError, setTermsError] = useState('')

  useEffect(() => {
    if (!accountsEnabled) { navigate('/', { replace: true }); return }
    if (customer) navigate(next || '/account', { replace: true })
  }, [customer, navigate, next, accountsEnabled])

  const copy = TITLES[mode] ?? TITLES.login
  const visual = VISUAL_COPY[mode] ?? VISUAL_COPY.login
  const registrationBlocked = mode === 'register' && !registrationOpen
  const termsLabel  = customerAccounts?.termsLinkLabel ?? DEFAULT_TERMS_LABEL
  const termsPrefix = customerAccounts?.termsConsentText ?? DEFAULT_TERMS_CONSENT

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setNotice('')
    setLoading(true)
    try {
      if (mode === 'login') {
        await login(email.trim(), password)
        navigate(next || '/account')
      } else if (mode === 'register') {
        // Same rule the API enforces, from the same module — so the message
        // under the field is the one the server would have returned.
        const checked = validateFullName({ firstName, lastName })
        if (!checked.ok) {
          setNameError({ [checked.field]: checked.error })
          setLoading(false)
          return
        }
        if (!acceptedTerms) {
          setTermsError(`Please accept the ${termsLabel} to continue.`)
          setLoading(false)
          return
        }
        await register(email.trim(), password, checked.firstName, checked.lastName, false, true)
        navigate(next || '/account')
      } else {
        await customerApi.requestPasswordReset(email.trim())
        setNotice('If an account exists for that email, a reset link is on its way. Check your inbox.')
      }
    } catch (err) {
      setError(err.message ?? 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  async function handleMagicLink() {
    if (!email.trim()) {
      setError('Enter your email first.')
      return
    }
    setError('')
    await customerApi.requestMagicLink(email.trim())
    setMagicSent(true)
  }

  const nextQuery = next ? `?next=${encodeURIComponent(next)}` : ''

  return (
    <div className="container account-auth">
      <SEO config={{ title: copy.title, robots: 'noindex, nofollow', titleRaw: true }} />

      <motion.div
        className="account-auth__console"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        <span className="account-auth__corner account-auth__corner--tl" aria-hidden="true" />
        <span className="account-auth__corner account-auth__corner--tr" aria-hidden="true" />
        <span className="account-auth__corner account-auth__corner--bl" aria-hidden="true" />
        <span className="account-auth__corner account-auth__corner--br" aria-hidden="true" />

        <div className="account-auth__visual">
          <div className="account-auth__scanline" aria-hidden="true" />
          <div className="account-auth__grid" aria-hidden="true" />

          <span className="account-auth__status">
            <motion.span
              className="account-auth__status-dot"
              animate={{ opacity: [1, 0.25, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              aria-hidden="true"
            />
            {visual.eyebrow}
          </span>

          <h2 className="account-auth__visual-headline">
            {visual.line1}
            <br />
            {visual.line2}
          </h2>
          <p className="account-auth__visual-body">{visual.body}</p>

          <div className="account-auth__visual-stats">
            {VISUAL_STATS.map((s) => (
              <div key={s.label} className="account-auth__visual-stat">
                <span className="account-auth__visual-stat-value">{s.value}</span>
                <span className="account-auth__visual-stat-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <Card hoverLift={false} className="account-auth__card">
          <span className="account-auth__form-eyebrow">{copy.title}</span>
          <h1>{copy.title}</h1>
          <p>{copy.sub}</p>

          {registrationBlocked ? (
            <p className="account-auth__notice">
              New sign-ups are closed right now. Already have an account? <Link to={`/account/login${nextQuery}`}>Sign in</Link>
            </p>
          ) : notice ? (
            <p className="account-auth__notice">{notice}</p>
          ) : (
            <motion.form onSubmit={handleSubmit} variants={fieldGroup} initial="hidden" animate="show">
              {mode === 'register' && (
                <motion.div variants={field} className="account-auth__names">
                  <Input
                    label="First name" required autoComplete="given-name"
                    value={firstName} error={nameError.firstName}
                    onChange={(e) => { setFirstName(e.target.value); setNameError((n) => ({ ...n, firstName: undefined })) }}
                  />
                  <Input
                    label="Last name" required autoComplete="family-name"
                    value={lastName} error={nameError.lastName}
                    onChange={(e) => { setLastName(e.target.value); setNameError((n) => ({ ...n, lastName: undefined })) }}
                  />
                </motion.div>
              )}
              <motion.div variants={field}>
                <Input label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </motion.div>
              {mode !== 'forgot' && (
                <motion.div variants={field}>
                  <Input label="Password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} error={error} />
                </motion.div>
              )}
              {mode === 'forgot' && error && <p className="account-auth__error">{error}</p>}

              {mode === 'register' && (
                <motion.div variants={field}>
                  <label className="account-auth__terms">
                    <input
                      type="checkbox"
                      checked={acceptedTerms}
                      aria-invalid={termsError ? 'true' : undefined}
                      onChange={(e) => { setAcceptedTerms(e.target.checked); if (e.target.checked) setTermsError('') }}
                    />
                    <span>
                      {termsPrefix}{' '}
                      {/* New tab on purpose — reading the terms must not throw
                          away the half-filled form behind it. */}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer">{termsLabel}</Link>.
                    </span>
                  </label>
                  {termsError && <p className="account-auth__error account-auth__error--terms">{termsError}</p>}
                </motion.div>
              )}

              <motion.div variants={field}>
                <Button type="submit" variant="primary" size="lg" loading={loading} className="account-auth__submit">
                  {mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create account' : 'Send reset link'}
                </Button>
              </motion.div>

              {mode === 'login' && (
                <motion.button variants={field} type="button" className="account-auth__magic" onClick={handleMagicLink}>
                  {magicSent ? 'Magic link sent — check your inbox' : 'Email me a sign-in link instead'}
                </motion.button>
              )}
            </motion.form>
          )}

          {mode === 'login' && (
            <p className="account-auth__switch">
              <Link to={`/account/forgot${nextQuery}`}>Forgot password?</Link>
              {registrationOpen && (
                <>
                  <br />
                  No account? <Link to={`/account/register${nextQuery}`}>Create one</Link>
                </>
              )}
            </p>
          )}
          {mode === 'register' && (
            <p className="account-auth__switch">
              Already have an account? <Link to={`/account/login${nextQuery}`}>Sign in</Link>
            </p>
          )}
          {mode === 'forgot' && (
            <p className="account-auth__switch">
              <Link to={`/account/login${nextQuery}`}>Back to sign in</Link>
            </p>
          )}
        </Card>
      </motion.div>
    </div>
  )
}
